import React, { useState, useEffect, useRef } from 'react';
import './ChatWidget.css';

const GREETING =
  "Hi, this is Harry from iLOVEthisGAME, glad you're here. I'm happy to answer any questions you may have.";

function TypingIndicator() {
  return (
    <div className="chat-bubble chat-bubble--bot chat-bubble--typing" aria-label="Assistant is typing">
      <span /><span /><span />
    </div>
  );
}

function BotAvatar() {
  return (
    <div className="chat-avatar" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        <circle cx="18" cy="6" r="2" fill="var(--chat-accent)" stroke="none" />
      </svg>
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen,    setIsOpen]    = useState(false);
  const [messages,  setMessages]  = useState([{ role: 'assistant', content: GREETING, timestamp: Date.now() }]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [pulse,     setPulse]     = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [sessionId] = useState(() => {
    try {
      let saved = sessionStorage.getItem('iltg_chat_session');
      if (!saved) {
        saved = `session_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        sessionStorage.setItem('iltg_chat_session', saved);
      }
      return saved;
    } catch (_e) {
      return `session_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    }
  });

  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);

  /* ── Auto-pulse after 1.5s ─────────────────────── */
  useEffect(() => {
    // Pulse the button first at 1.5 s
    const pulseTimer = setTimeout(() => setPulse(true), 1500);

    return () => {
      clearTimeout(pulseTimer);
    };
  }, []);

  /* ── Scroll to bottom whenever messages change ───────────────────────── */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  /* ── Focus input when panel opens ───────────────────────────────────── */
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  /* ── Toggle open/close ───────────────────────────────────────────────── */
  const toggleOpen = () => {
    setPulse(false);
    setIsOpen(prev => !prev);
    if (!hasOpened) setHasOpened(true);
  };

  /* ── Send a message ──────────────────────────────────────────────────── */
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text, timestamp: Date.now() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('https://us-central1-iltg-website-web.cloudfunctions.net/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updated, sessionId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Server error');

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.reply || "I'm here to help with Playermaker questions. How can I assist you?" }
      ]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'I apologize, I am temporarily having trouble connecting. Please try asking again in a moment, or reach out to Jason directly at support@ilovethisgame.ca.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* ── Chat Panel ─────────────────────────────────────────────────── */}
      <div
        className={`chat-panel ${isOpen ? 'chat-panel--open' : ''}`}
        role="dialog"
        aria-label="ILTG AI Customer Support"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="chat-header-avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </div>
            <div>
              <p className="chat-header-name">Harry, iLOVEthisGAME</p>
              <p className="chat-header-status">
                <span className="status-dot" />
                Online · Playermaker Canada
              </p>
            </div>
          </div>
          <button
            className="chat-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="chat-messages" role="log" aria-live="polite">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`chat-row chat-row--${msg.role}`}
            >
              {msg.role === 'assistant' && <BotAvatar />}
              <div className={`chat-bubble chat-bubble--${msg.role === 'assistant' ? 'bot' : 'user'}`}>
                {msg.content}
              </div>
            </div>
          ))}

          {loading && (
            <div className="chat-row chat-row--assistant">
              <BotAvatar />
              <TypingIndicator />
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="chat-input-row">
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder="Ask about Playermaker, shipping, straps…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            aria-label="Type your message"
            disabled={loading}
          />
          <button
            className="chat-send-btn"
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>

        <p className="chat-footer-note">
          Need a human? <a href="https://wa.me/17789091613?text=Hi%20Jason%2C%20I%20have%20a%20question%20about%20iLOVEthisGAME" target="_blank" rel="noopener noreferrer">WhatsApp Jason</a> · <a href="mailto:support@ilovethisgame.ca">Email Support</a>
        </p>
      </div>

      {/* ── Floating Toggle Button ────────────────────────────────────────── */}
      <button
        className={`chat-fab ${pulse ? 'chat-fab--pulse' : ''} ${isOpen ? 'chat-fab--open' : ''}`}
        onClick={toggleOpen}
        aria-label={isOpen ? 'Close AI support chat' : 'Open AI support chat'}
        aria-expanded={isOpen}
      >
        {/* Chat icon */}
        <span className="chat-fab-icon chat-fab-icon--chat">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
        </span>
        {/* Close X */}
        <span className="chat-fab-icon chat-fab-icon--close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </span>

        {/* Unread badge (shows before first open) */}
        {!hasOpened && <span className="chat-fab-badge">1</span>}
      </button>
    </>
  );
}
